"use client";

import React, { useEffect, useState } from "react";
import { useClientValue } from "@/hooks/useClientValue";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { cva } from "class-variance-authority";
import { usePathname } from "next/navigation";
import {
	Button,
	textVariants,
	Link,
	LinkVariant,
	Breakpoint,
	useValueAtBreakpoint,
} from "@jasperlabs/ui";
import { NavLink } from "@/components/organisms/navigation/NavigationLink";
import dynamic from "next/dynamic";
import { useSession } from "@/contexts/session";
import RenderIfCondition from "@/components/organisms/RenderIfCondition";
import LoginLink from "@/components/organisms/LoginLink";
import {
	TenantLogoLink,
	AccountDropdownMenu,
} from "@/components/organisms/navigation";
import {
	BehindFeature,
	useFeatureFlag,
} from "@/components/utilities/featureFlag";
import { useCrossEntityNotificationsCount } from "@/components/utilities/notifications";

import AppHeader from "./AppHeader";

import { InvestAndTradeCheckDocument } from "./AppHeaderPrimary.generated";

const MobileNavigation = dynamic(
	() => import("@/components/organisms/navigation/MobileNavigation"),
	{ ssr: false },
);

const pillClassName = cva(
	"flex items-center justify-center px-[6px] h-4 min-w-[16px] rounded-full text-xs leading-none focus:!no-underline",
	{
		variants: {
			variant: {
				default: "bg-[#FFFFFF33] text-white",
				warning: "bg-warning text-white",
			},
		},
	},
);

const NavItemWithCountPill = ({
	name,
	count,
	variant = "default",
}: {
	name: string;
	count: number;
	variant?: "default" | "warning";
}) => (
	<div className="flex items-center gap-1">
		{name}
		<RenderIfCondition condition={count > 0}>
			<div data-testid="nav-count-pill" className={pillClassName({ variant })}>
				{count}
			</div>
		</RenderIfCondition>
	</div>
);

type AppHeaderPrimaryProps = {
	loginUrl?: string;
};

function AppHeaderPrimary({ loginUrl }: AppHeaderPrimaryProps) {
	const { hasAuthenticatedSession } = useSession();
	const [isModalOpen] = useState(false);
	const isClient = useClientValue(
		() => true,
		() => false,
	);
	const pathname = usePathname();

	const { data, refetch } = useQuery(InvestAndTradeCheckDocument);

	const isDocumentsHidden = useFeatureFlag("HIDE_DOCUMENTS");
	const crossEntityNotificationsCount = useCrossEntityNotificationsCount({
		skip: !hasAuthenticatedSession || isModalOpen,
	});

	const buttonSize = useValueAtBreakpoint<"sm" | "lg">({
		[Breakpoint.XS]: "sm",
		[Breakpoint.SM]: "sm",
		[Breakpoint.MD]: "lg",
		[Breakpoint.LG]: "lg",
		[Breakpoint.XL]: "lg",
	});

	useEffect(() => {
		refetch();
	}, [pathname, refetch]);

	if (!isClient) {
		return null;
	}

	const offersCount = Number(
		data?.offers?.filter((offer) => !offer.hideOffer)?.length,
	);
	const openOffersCount = Number(
		data?.offers?.filter((offer) => !offer.hideOffer && offer.status === "OPEN")
			?.length,
	);
	const sellOrdersCount = Number(
		data?.sellOrders?.filter((sellOrder) => sellOrder.status === "OPEN")
			?.length,
	);

	const hasOffers = offersCount > 0;
	const hasSellOrders = sellOrdersCount > 0;

	if (hasAuthenticatedSession && !isModalOpen) {
		return (
			<AppHeader data-testid="app-header-primary">
				<AppHeader.Layout
					left={
						<div>
							<TenantLogoLink />
						</div>
					}
					center={
						<div className="hidden lg:flex items-center justify-end gap-[clamp(0.5rem,calc(16vw_-_145px),1.25rem)] w-full min-w-0 whitespace-nowrap">
							<RenderIfCondition condition={!!hasOffers}>
								<Link
									as={NavLink}
									href="/invest"
									variant={LinkVariant.NavInverted}
									className={textVariants({ variant: "subheading-sm" })}
									partialMatch
								>
									<NavItemWithCountPill name="Invest" count={openOffersCount} />
								</Link>
							</RenderIfCondition>
							<RenderIfCondition condition={!!hasSellOrders}>
								<Link
									as={NavLink}
									href="/trade"
									variant={LinkVariant.NavInverted}
									className={textVariants({ variant: "subheading-sm" })}
									partialMatch
								>
									<NavItemWithCountPill name="Trade" count={sellOrdersCount} />
								</Link>
							</RenderIfCondition>
							<Link
								as={NavLink}
								href="/portfolio"
								variant={LinkVariant.NavInverted}
								className={textVariants({ variant: "subheading-sm" })}
								partialMatch
							>
								Portfolio
							</Link>
							<Link
								as={NavLink}
								href="/orders"
								variant={LinkVariant.NavInverted}
								className={textVariants({ variant: "subheading-sm" })}
								partialMatch
							>
								Orders
							</Link>
							{!isDocumentsHidden && (
								<Link
									as={NavLink}
									href="/documents"
									variant={LinkVariant.NavInverted}
									className={textVariants({ variant: "subheading-sm" })}
								>
									Documents
								</Link>
							)}
							<Link
								as={NavLink}
								href="/holding-movements"
								variant={LinkVariant.NavInverted}
								className={textVariants({ variant: "subheading-sm" })}
							>
								Holding Movements
							</Link>
							<BehindFeature feature="DISTRIBUTION_TRANSACTIONS_TABLE">
								<Link
									as={NavLink}
									href="/distributions"
									variant={LinkVariant.NavInverted}
									className={textVariants({ variant: "subheading-sm" })}
								>
									Distributions
								</Link>
							</BehindFeature>
							<Link
								as={NavLink}
								href="/notifications"
								variant={LinkVariant.NavInverted}
								className={textVariants({ variant: "subheading-sm" })}
								partialMatch
							>
								<NavItemWithCountPill
									name="Alerts"
									count={crossEntityNotificationsCount}
									variant="warning"
								/>
							</Link>
						</div>
					}
					right={
						<div className="flex items-center justify-end ml-auto sm:gap-2 shrink-0">
							<div className="hidden lg:flex gap-2 items-center">
								<AccountDropdownMenu />
							</div>
							<div className="lg:hidden">
								<MobileNavigation
									hasOffers={hasOffers}
									sellOrdersCount={sellOrdersCount}
									offersCount={openOffersCount}
								/>
							</div>
						</div>
					}
				/>
			</AppHeader>
		);
	}

	return (
		<AppHeader data-testid="app-header-primary">
			<AppHeader.Layout
				left={
					<div className="lg:min-w-[275px]">
						<TenantLogoLink />
					</div>
				}
				right={
					<LoginLink href={loginUrl}>
						<Button
							variant="inverted-secondary"
							size={buttonSize}
							weight="medium"
						>
							Login
						</Button>
					</LoginLink>
				}
				variant="simple"
			/>
		</AppHeader>
	);
}

gql`
	query InvestAndTradeCheck {
		offers {
			id
			hideOffer
			status
		}
		sellOrders {
			id
			status
		}
	}
`;

export default AppHeaderPrimary;
